import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactApexChart from 'react-apexcharts';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { UnixTime, TimeSpan } from 'src/dataCache/time';
import {
  CurrentPriceSeries,
  fetchCurrentPriceHistory
} from './currentPriceData';

interface CurrentPriceChartProps {
  id: number;
}

const HISTORY_DAYS = 7;
const CHART_ID = 'current-price-history';

function CurrentPriceChart(props: CurrentPriceChartProps) {
  const intl = useIntl();
  const [series, setSeries] = useState<CurrentPriceSeries | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const end = UnixTime.now();
    const start = end.earlier(TimeSpan.fromDays(HISTORY_DAYS));

    fetchCurrentPriceHistory(props.id, start, end)
      .then((result) => {
        if (!cancelled) {
          setSeries(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSeries({ data: [], min: 0, max: 0 });
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.id]);

  // Snap the x-axis back to the full history window after the user drag-zooms.
  const handleResetZoom = () => {
    if (!series || series.data.length === 0) return;
    const minX = series.data[0][0];
    const maxX = series.data[series.data.length - 1][0];
    ApexCharts.exec(CHART_ID, 'zoomX', minX, maxX);
  };

  const title = (
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
      <FormattedMessage
        id="currentPriceHistory"
        defaultMessage="Current Price (last 7 days)"
      />
    </Typography>
  );

  if (loading) {
    return (
      <Box sx={{ mb: 2 }}>
        {title}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 280
          }}
        >
          <CircularProgress size={36} sx={{ color: '#ffc04d' }} />
        </Box>
      </Box>
    );
  }

  if (!series || series.data.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        {title}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <FormattedMessage
            id="currentPriceNoData"
            defaultMessage="No price history available for the last 7 days."
          />
        </Typography>
      </Box>
    );
  }

  const options: ApexOptions = {
    chart: {
      id: CHART_ID,
      type: 'line',
      height: 300,
      // Toolbar hidden — its zoom glyphs are easy to miss; we expose an explicit
      // "Reset zoom" button instead. Drag-to-zoom still works via zoom.enabled.
      toolbar: { show: false },
      zoom: { enabled: true, type: 'x', autoScaleYaxis: true }
    },
    dataLabels: { enabled: false },
    // Spot price is constant within each pricing interval -> stepped line
    stroke: { curve: 'stepline', width: 2 },
    colors: ['#5569ff'],
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          month: "MMM 'yy",
          day: 'dd MMM',
          hour: 'HH:mm'
        }
      }
    },
    yaxis: {
      min: 0,
      tickAmount: 5,
      title: { text: 'CHF/kWh', style: { fontSize: '12px' } },
      labels: { formatter: (v: number) => (v == null ? '' : v.toFixed(3)) }
    },
    tooltip: {
      x: { format: 'dd MMM HH:mm' },
      y: {
        formatter: (v: number) =>
          v == null || Number.isNaN(v) ? '-' : v.toFixed(3) + ' CHF/kWh'
      }
    },
    grid: { padding: { top: 10 } }
  };

  const chartSeries = [
    {
      name: intl.formatMessage({ id: 'currentPrice' }),
      data: series.data
    }
  ];

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          <FormattedMessage
            id="currentPriceHistory"
            defaultMessage="Current Price (last 7 days)"
          />
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={handleResetZoom}
        >
          <FormattedMessage id="resetZoom" defaultMessage="Reset zoom" />
        </Button>
      </Box>
      <ReactApexChart
        options={options}
        series={chartSeries}
        type="line"
        height={300}
      />
    </Box>
  );
}

export default CurrentPriceChart;
